// src/templates/mobile/DashboardMobile.jsx
import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useDashboardManutencao } from "../../hooks/useDashboardManutencao";

function fmtInt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("pt-BR");
}

function fmtHours(n, casas = 1) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return v.toFixed(casas).replace(".", ",");
}

function KpiTile({ title, value, hint, accent, badge }) {
  const letter = (badge || String(title || "").slice(0, 1)).toUpperCase();
  return (
    <div className="pc-card pc-card-ab" style={{ borderLeft: `6px solid ${accent}` }}>
      <div className="pc-card-ab-badge" style={{ background: accent }}>{letter}</div>
      <div className="pc-card-ab-main">
        <div className="pc-card-ab-value" style={{ fontSize: 20, fontWeight: 900 }}>{value}</div>
        <div className="pc-card-ab-label" style={{ fontSize: 12, opacity: 0.85 }}>{title}</div>
        {hint ? <div style={{ fontSize: 11, opacity: 0.7 }}>{hint}</div> : null}
      </div>
      <div />
    </div>
  );
}

export default function DashboardMobile() {
  const { usuario } = useAuth();
  const { loading, error, kpis, topOsAbertas } = useDashboardManutencao({ grupoId: usuario?.grupoId });

  return (
    <section className="m-dash">
      {/* 1-4 (mesmos cards do desktop) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <KpiTile
          title="OS em aberto"
          value={loading ? "..." : fmtInt(kpis.osAbertasAgora)}
          hint="Total agora"
          accent="#38bdf8"
          badge="OS"
        />
        <KpiTile
          title="Máquinas paradas"
          value={loading ? "..." : fmtInt(kpis.maquinasParadasAgora)}
          hint="Em aberto agora"
          accent="#ef4444"
          badge="MP"
        />
        <KpiTile
          title="Aguardando material"
          value={loading ? "..." : fmtInt(kpis.aguardandoMaterialAgora)}
          hint="Status atual"
          accent="#f59e0b"
          badge="AM"
        />
        <KpiTile
          title="Paradas no mês"
          value={loading ? "..." : `${fmtHours(kpis.paradasHorasMes, 1)} h`}
          hint="Total aproximado"
          accent="#a855f7"
          badge="H"
        />
      </div>

      {/* 5-6 (linha A/B igual ao desktop) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>
        <KpiTile
          title="OS abertas no mês"
          value={loading ? "..." : fmtInt(kpis.osAbertasMes)}
          accent="#38bdf8"
          badge="A"
        />
        <KpiTile
          title="OS finalizadas no mês"
          value={loading ? "..." : fmtInt(kpis.osFinalizadasMes)}
          accent="#a855f7"
          badge="B"
        />
      </div>

      {error ? (
        <div className="pc-alert pc-alert-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      ) : null}

      {/* 7-8 (cards informativos) */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 12, marginTop: 12 }}>
        <div className="pc-card" style={{ padding: 14, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div>
              <h3 style={{ margin: 0 }}>Paradas críticas (OS) — Top 3 agora</h3>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>Ordenado por maior tempo de parada (aproximação).</div>
            </div>
            <span className="pc-card-chart-tag">Em aberto</span>
          </div>

          {loading ? (
            <div style={{ marginTop: 12, opacity: 0.85 }}>Carregando...</div>
          ) : topOsAbertas.length ? (
            <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
              {topOsAbertas.map((os) => (
                <div key={os.id} className="pc-chart-item" style={{ minWidth: 0 }}>
                  <div className="pc-chart-item-title" title={os.titulo}>{os.titulo}</div>
                  <div className="pc-chart-item-sub">
                    <span title={os.maquinaNome}>{os.maquinaNome}</span>
                    <span>•</span>
                    <span>{fmtHours(os.horas, 1)} h</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: 12, opacity: 0.8 }}>Sem OS críticas em aberto.</div>
          )}
        </div>

        <div className="pc-card" style={{ padding: 14 }}>
          <h3 style={{ margin: 0 }}>Indicadores rápidos (mês)</h3>
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <div className="pc-side-item">
              <span>MTBF (mês)</span>
              <strong>{loading ? "..." : (kpis.mtbfHorasMes == null ? "-" : `${fmtHours(kpis.mtbfHorasMes, 1)} h`)}</strong>
            </div>
            <div className="pc-side-item">
              <span>MTTR (mês)</span>
              <strong>{loading ? "..." : (kpis.mttrHorasMes == null ? "-" : `${fmtHours(kpis.mttrHorasMes, 1)} h`)}</strong>
            </div>
            <div className="pc-side-item">
              <span>Maior parada (evento)</span>
              <strong>{loading ? "..." : `${fmtHours(kpis.maiorParadaHorasMes, 1)} h`}</strong>
            </div>
            <div className="pc-side-item">
              <span>Máquina mais crítica</span>
              <strong title={kpis.maquinaMaisCriticaMes}>{loading ? "..." : kpis.maquinaMaisCriticaMes}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
